/** @jsxImportSource react */
import React, { FC, PropsWithChildren, useState, useEffect } from 'react';
import { IntlProvider as ReactIntlProvider, IntlConfig } from 'react-intl';
import {
  useIntlProviderData,
  IntlProviderDataResult,
} from './useIntlProviderData';

export type CoordinatedIntlProviderProps = PropsWithChildren &
  Partial<IntlConfig>;

/**
 * Drop-in replacement for react-intl's IntlProvider
 * Automatically coordinates with other instances to prevent conflicts
 * Always provides IntlProvider context so useIntl() works throughout the tree
 */
const CoordinatedIntlProvider: FC<CoordinatedIntlProviderProps> = ({
  children,
  ...intlProps
}) => {
  const [providerData, setProviderData] =
    useState<IntlProviderDataResult | null>(null);

  useEffect(() => {
    useIntlProviderData().then(setProviderData);
  }, []);

  if (!providerData) {
    return null;
  }

  return (
    <ReactIntlProvider
      messages={providerData.localeData.messages || {}}
      locale={providerData.localeData.currentLocale}
      defaultLocale="en"
      {...intlProps}
    >
      {children}
    </ReactIntlProvider>
  );
};

export { CoordinatedIntlProvider as IntlProvider };
export default CoordinatedIntlProvider;
